import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

// The customer-facing quotation as a PDF, rendered from a frozen
// quotation version: lines with HSN and GST, totals by slab, the 04.01
// SOP terms and bank details. Nothing internal is on the version, so
// nothing internal can be on the page.

const s = StyleSheet.create({
  page: { padding: 36, fontSize: 9.5, fontFamily: "Helvetica", color: "#1e2a23" },
  company: { fontSize: 15, fontFamily: "Helvetica-Bold", color: "#1f6b3a" },
  sub: { color: "#555", marginBottom: 12 },
  row: { flexDirection: "row" },
  meta: { width: "50%" },
  h: { fontFamily: "Helvetica-Bold", marginTop: 12, marginBottom: 4 },
  table: { marginTop: 8, borderTopWidth: 1, borderColor: "#999" },
  tr: { flexDirection: "row", borderBottomWidth: 0.5, borderColor: "#bbb", paddingVertical: 3 },
  th: { fontFamily: "Helvetica-Bold", backgroundColor: "#eaf5ec" },
  c1: { width: "5%" },
  c2: { width: "33%" },
  c3: { width: "10%" },
  c4: { width: "12%" },
  c5: { width: "8%", textAlign: "right" },
  c6: { width: "10%", textAlign: "right" },
  c7: { width: "11%", textAlign: "right" },
  c8: { width: "5%", textAlign: "right" },
  c9: { width: "6%", textAlign: "right" },
  totals: { marginTop: 6, alignItems: "flex-end" },
  tl: { flexDirection: "row", width: 220, justifyContent: "space-between", paddingVertical: 1.5 },
  grand: { fontFamily: "Helvetica-Bold", borderTopWidth: 1, borderColor: "#999", marginTop: 2, paddingTop: 3 },
  terms: { marginTop: 14, color: "#333" },
  small: { fontSize: 8.5, color: "#555" },
});

const inr = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const QuotationPDF = ({ version, orgName, siteName }) => {
  const t = version.terms || {};
  const bank = t.bank || {};
  const slabs = Object.entries(version.totals.gstBySlab || {}).filter(([k]) => Number(k) > 0).sort(([a], [b]) => Number(a) - Number(b));
  return (
    <Document title={`${version.quoteNumber} - ${orgName}`}>
      <Page size="A4" style={s.page}>
        <Text style={s.company}>{t.company || "ManBhawan Eco Ventures LLP"}</Text>
        <Text style={s.sub}>ManBhawan Nursery · Noida / Ghaziabad · GSTIN 09AAYFP3567B1ZR</Text>
        <View style={s.row}>
          <View style={s.meta}>
            <Text style={s.h}>Quotation</Text>
            <Text>{version.quoteNumber}</Text>
            <Text>Date: {version.quoteDate}</Text>
            <Text>Valid till: {version.validTill}</Text>
          </View>
          <View style={s.meta}>
            <Text style={s.h}>Customer</Text>
            <Text>{orgName}</Text>
            {siteName ? <Text>{siteName}</Text> : null}
          </View>
        </View>

        <View style={s.table}>
          <View style={[s.tr, s.th]}>
            <Text style={s.c1}>#</Text><Text style={s.c2}>Item</Text><Text style={s.c3}>HSN</Text><Text style={s.c4}>Size</Text><Text style={s.c5}>Qty</Text><Text style={s.c6}>Rate</Text><Text style={s.c7}>Amount</Text><Text style={s.c8}>GST</Text><Text style={s.c9}>Tax</Text>
          </View>
          {version.lines.map((l, i) => (
            <View style={s.tr} key={l.id || i}>
              <Text style={s.c1}>{i + 1}</Text>
              <Text style={s.c2}>{l.item}</Text>
              <Text style={s.c3}>{l.hsnCode || ""}</Text>
              <Text style={s.c4}>{l.size || ""}</Text>
              <Text style={s.c5}>{l.quantity ?? ""}</Text>
              <Text style={s.c6}>{inr(l.rate)}</Text>
              <Text style={s.c7}>{inr(l.amount)}</Text>
              <Text style={s.c8}>{l.gstPct === null || l.gstPct === undefined ? "" : `${l.gstPct}%`}</Text>
              <Text style={s.c9}>{inr(l.taxAmount)}</Text>
            </View>
          ))}
        </View>

        <View style={s.totals}>
          <View style={s.tl}><Text>Taxable value</Text><Text>₹ {inr(version.totals.taxable)}</Text></View>
          {slabs.map(([slab, amt]) => <View style={s.tl} key={slab}><Text>GST @ {slab}%</Text><Text>₹ {inr(amt)}</Text></View>)}
          <View style={[s.tl, s.grand]}><Text>Grand total</Text><Text>₹ {inr(version.totals.grand)}</Text></View>
        </View>

        <View style={s.terms}>
          <Text style={s.h}>Terms</Text>
          <Text>This quotation is valid for {t.validityDays || 15} days, until {version.validTill}.</Text>
          {t.paymentNote ? <Text>{t.paymentNote}</Text> : null}
          <Text>GST charged as shown, per HSN classification of each item.</Text>
          {t.notes ? <Text>Notes: {t.notes}</Text> : null}
          <Text style={s.h}>Bank details</Text>
          <Text>Beneficiary: {bank.beneficiary || t.company}</Text>
          <Text>Account: {bank.account}   IFSC: {bank.ifsc}</Text>
          <Text style={[s.small, { marginTop: 14 }]}>Generated by HortiHub by ManBhawan.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default QuotationPDF;
